import { ActionCard } from '@pcln/cards'
import { Carousel, ActionCard as HorizonActionCard } from '@pcln/horizon'
import { useEffect, useMemo } from 'react'

import useSeti from '@/hooks/useSeti'
import analytics from '@/shared-utils/analytics'

import { fireRecentSearchCardClickEvent, fireRecentSearchLoadEvent } from './ga4'
import type { RecentTripSearches } from './types'
import { getRecentSearchDisplayProps, onCardClick } from './utils'

const PRODUCT_ICON: Record<string, string> = {
  FLY: 'Flight',
  DRIVE: 'Car',
  STAY: 'Hotel',
} as const

export default function RecentSearchCarousel({
  pageName,
  items,
}: Readonly<{
  pageName: 'homepage' | 'landing'
  items: ReadonlyArray<RecentTripSearches>
}>) {
  useEffect(() => {
    if (items.length > 0) {
      fireRecentSearchLoadEvent({ searchItems: items, pageName })
    }
  }, [items, pageName])

  const isHorizonRecentSearch = useSeti({ experimentCode: 'WEB_HP_RECENTSEARCHES_HORIZON' }) === 'VARIANT'

  const slides = useMemo(
    () =>
      items.reduce<{ id: string; childrenNodes: React.ReactNode }[]>((validSlides, item, index) => {
        try {
          const { overline, heading, icon, actionText, relativeUrl } = getRecentSearchDisplayProps(item)
          const mediaContent = PRODUCT_ICON[item.productType]
          return [
            ...validSlides,
            {
              id: `recent-search-${index}-${overline}`,
              childrenNodes: isHorizonRecentSearch ? (
                <HorizonActionCard
                  role='link'
                  href={relativeUrl}
                  anchorProps={{ target: '_blank', rel: 'noopener noreferrer' }}
                  eyebrow={overline}
                  heading={heading}
                  body={actionText}
                  mediaType='icon'
                  mediaContent={mediaContent}
                  onClick={() => {
                    fireRecentSearchCardClickEvent({
                      searchItem: item,
                      productType: item.productType,
                      pageName: pageName,
                    })
                  }}
                  className='w-full'
                />
              ) : (
                <ActionCard
                  headingWidth={['194px', '100px', 'null', '190px', '200px', '250px']}
                  icon={icon}
                  heading={heading}
                  actionText={actionText}
                  overline={overline}
                  onClick={() => {
                    onCardClick(item, relativeUrl, pageName, item.productType)
                  }}
                  handleSubmitCTA={() => {
                    onCardClick(item, relativeUrl, pageName, item.productType)
                  }}
                  className='full-width'
                />
              ),
            },
          ]
        } catch (err) {
          analytics.logError({
            message: 'Error in getRecentSearchDisplayProps',
            errorMessage: err instanceof Error ? err.message : String(err),
            logObject: {
              error: JSON.stringify(err),
              item,
            },
          })
          return validSlides
        }
      }, []),
    [items, pageName, isHorizonRecentSearch],
  )

  return (
    <Carousel
      ariaLabel='Your recent searches'
      dragFree
      numSlides='one-two-three'
      slides={slides}
      slideWidth='same'
      pagination='numbers'
      type='content'
    />
  )
}
